<template>
    <b-modal
        ref="supplier-modal"
        :ok-title="$t('Component(NewSupplierModalButton):add')"
        :cancel-title="$t('Component(Button):cancel')"
        @ok="save"
        centered
        size="lg"
        :ok-disabled="isAddButtonDisabled"
        :title="title"
        @hidden="onModalClose"
    >
        <Overlay :show="showLoading">
            <b-collapse :visible="showExistingSupplierMessage">
                <b-alert
                    :variant="existingSupplierMessageVariant"
                    show
                >
                    <div class="alert-body">
                        {{ existingSupplierMessage }}
                        <div v-if="existingSupplier" class="text-right pt-1">
                            <Button class="btn-sm" @click="useExistingSupplier">{{ $t('Component(Button):use-existing-supplier') }}</Button>
                        </div>
                    </div>
                </b-alert>
            </b-collapse>
            <b-collapse :visible="showExistingSubscriberMessage">
                <b-alert
                    variant="warning"
                    show
                >
                    <div class="alert-body">
                        <div class="mb-1">
                            {{ existingSubscriberOrganizationMessage }}
                        </div>
                        <v-select
                            v-model="selectedLookupItem"
                            :clearable="false"
                            :options="subscriberLookupResult"
                            :getOptionLabel="(item) => item.organization.title"
                        />
                        <div v-if="selectedLookupItem" class="pt-1 text-right">
                            <Button class="btn-sm" @click="useExistingSubscriberOrganization">
                                {{ $t('Component(Button):use-existing-subscriber-organization') }}
                            </Button>
                        </div>
                    </div>
                </b-alert>
            </b-collapse>
            <validation-observer ref="supplier">
                <b-form>
                    <b-form-group
                        :label="$t('Component(SupplierSelector):label-organization-name')"
                        label-for="organizationName"
                    >
                        <validation-provider
                            #default="{ errors }"
                            :name="$t('Component(SupplierSelector):label-organization-name')"
                            rules="required|max:255|min:3"
                        >
                            <b-form-input
                                :state="errors.length > 0 ? false:null"
                                id="organizationName"
                                :placeholder="$t('Component(SupplierSelector):placeholder-organization-name')"
                                @change="nameHasChanged"
                                v-model="supplier.title"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>
                    </b-form-group>
                    <b-form-group
                        :label="$t('Component(SupplierSelector):label-organization-state')"
                        label-for="organizationState"
                    >
                        <treeselect
                            id="organizationState"
                            :options="states"
                            noChildrenText=""
                            :normalizer="normalize"
                            :disable-branch-nodes="true"
                            v-model="supplier.state"
                            :show-count="true"
                            @input="stateChange"
                        />
                        <small class="text-danger" v-if="stateErrorMessage">{{ stateErrorMessage }}</small>
                    </b-form-group>
                    <b-form-group
                        :label="$t('Component(Label):domain')"
                        label-for="domain"
                    >
                        <validation-provider
                            #default="{ errors }"
                            :name="$t('Component(Label):domain')"
                            rules="required|max:255|min:3"
                        >
                            <b-form-input
                                :state="errors.length > 0 ? false:null"
                                id="domain"
                                v-model="supplier.domain"
                                @change="domainChange"
                                :placeholder="$t('Component(Placeholder):domain')"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                            <small class="text-danger">{{ domainError }}</small>
                        </validation-provider>
                    </b-form-group>
                    <LegalIdentity
                        ref="legalIdentity"
                        :vertical="true"
                        :identity="supplier.legalIdentity"
                        :errors="legalIdentityErrors"
                        @input="onLegalIdentityChange"
                    />
                    <b-form-group
                        :label="$t('Component(SupplierSelector):label-contact-name')"
                        label-for="contactName"
                    >
                        <validation-provider
                            #default="{ errors }"
                            :name="$t('Component(SupplierSelector):label-contact-name')"
                            rules="required|max:255|min:2"
                        >
                            <b-form-input
                                :state="errors.length > 0 ? false:null"
                                id="contactName"
                                :placeholder="$t('Component(SupplierSelector):placeholder-contact-name')"
                                v-model="contact.name"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>
                    </b-form-group>
                    <b-form-group
                        :label="$t('Component(SupplierSelector):label-contact-email')"
                        label-for="contactEmail"
                    >
                        <validation-provider
                            #default="{ errors }"
                            :name="$t('Component(SupplierSelector):label-contact-email')"
                            rules="required|max:255|min:2|email"
                        >
                            <b-form-input
                                :state="errors.length > 0 ? false:null"
                                id="contactEmail"
                                @input="onContactEmailChange"
                                :placeholder="$t('Component(SupplierSelector):placeholder-contact-email')"
                                v-model="communicationChannel.channel"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>
                        <b-collapse :visible="showContactEmailDomainDiffersToOrganizationDomainMessage">
                            <b-alert
                                class="mt-1"
                                variant="warning"
                                show
                            >
                                <div class="alert-body">
                                    {{ $t('Component(Text):supplier-contact-domain-differs-with-organization-domain') }}
                                </div>
                            </b-alert>
                        </b-collapse>
                    </b-form-group>
                </b-form>
            </validation-observer>
        </Overlay>
    </b-modal>
</template>

<script>
import {
    BFormGroup, BButton, BInputGroup, BFormInput, BInputGroupPrepend, BModal, BForm, BBadge, BAlert, BCollapse
} from 'bootstrap-vue'
import Treeselect from '@riophae/vue-treeselect'
import {SubscriberApi, SupplyChain} from "@core/api";
import {CommunicationType} from "@/libs/supply-chain";
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {required, max, min, email} from '@validations'
import backendResponse from "@core/mixins/ui/backend-response";
import LegalIdentity from "@core/components/common/LegalIdentity.vue";
import Button from "@core/components/common/Button.vue";
import vSelect from 'vue-select'
import Overlay from "@core/components/common/Overlay.vue";

export default {
    mixins: [backendResponse],
    components: {
        Overlay, BCollapse, Button, LegalIdentity, BFormGroup, BButton, ValidationProvider, ValidationObserver,
        BInputGroup, BFormInput, BInputGroupPrepend, BModal, BForm, BBadge, BAlert, vSelect, Treeselect,
    },
    props: {
        title: {
            default() {
                return this.$t('Component(NewSupplierModal):title')
            }
        }
    },
    data() {
        return {
            stateErrorMessage: null,
            states: [],
            supplier: this.emptySupplier(),
            contact: this.emptyContact(),
            communicationChannel: this.emptyChannel(),
            required, max, min, email,
            legalIdentityTimer: null,
            showExistingSupplierMessage: false,
            existingSupplierMessage: false,
            existingSupplierMessageVariant: 'danger',
            isAddButtonDisabled: false,

            existingSupplier: null,

            showExistingSubscriberMessage: false,
            isSubscriberFullyMatched: false,
            subscriberLookupResult: [],
            selectedLookupItem: null,

            showContactEmailDomainDiffersToOrganizationDomainMessage: false,

            showLoading: false,
            contactEmailTimeoutId: null,
            legalIdentityErrors: [],
            domainError: ''
        };
    },
    methods: {
        nameHasChanged() {
            this.checkExistingSupplier();
        },
        domainChange() {
            this.checkExistingSupplier();
            this.checkContactDomainToOrganizationDomain();
        },
        onLegalIdentityChange(le) {
            this.supplier.legalIdentity = le;

            clearTimeout(this.legalIdentityTimer);
            this.legalIdentityTimer = setTimeout(() => {
                this.checkExistingSupplier();
            }, 2000);
        },
        onContactEmailChange() {
            clearTimeout(this.contactEmailTimeoutId);
            this.contactEmailTimeoutId = setTimeout(() => {
                this.checkContactDomainToOrganizationDomain();
            }, 1000);
        },
        stateChange() {
            if (this.supplier.state) {
                this.stateErrorMessage = null;
            }
        },
        checkContactDomainToOrganizationDomain() {
            this.showContactEmailDomainDiffersToOrganizationDomainMessage = false;
            if (this.supplier.domain && this.communicationChannel && this.communicationChannel.channel) {
                if (!this.communicationChannel.channel.endsWith(this.supplier.domain)) {
                    this.showContactEmailDomainDiffersToOrganizationDomainMessage = true;
                }
            }
        },
        checkSuppliersDomain() {
            return this
                .searchExistingSupplier({domain: this.supplier.domain})
                .then((data) => {
                    this.existingSupplier = null;
                    if (data.exists) {
                        this.showExistingSubscriberMessage = false;
                        this.showExistingSupplierMessage = true;
                        this.existingSupplierMessageVariant = 'warning';
                        this.existingSupplierMessage = this.$t('Component(Text):supplier-with-domain-exists', {
                            name: data.supplier.title,
                            domain: data.supplier.domain
                        });
                        this.existingSupplier = data.supplier;
                    } else {
                        this.showExistingSupplierMessage = false;
                        this.lookupSubscriber();
                    }
                })
                .finally(() => this.showLoading = false)
            ;
        },
        lookupSubscriber() {
            return SubscriberApi
                .lookup(this.supplier.title, this.supplier.domain, this.supplier.legalIdentity)
                .then(r => {
                    const data = r.data;
                    this.isSubscriberFullyMatched = false;
                    this.showExistingSubscriberMessage = false;
                    this.isSubscriberFullyMatched = data.fullMatch;

                    if (data.items.length > 0) {
                        this.showExistingSubscriberMessage = true;
                        this.selectedLookupItem = data.items[0];
                        this.subscriberLookupResult = data.items;

                        //  if full match by title or legal identity then supplier can not be created
                        if (this.isSubscriberFullyMatched && (data.metadata.matchTitle || data.metadata.matchLegalIdentity)) {
                            this.isAddButtonDisabled = true;
                        }
                    }

                    if (this.isSubscriberFullyMatched) {
                        if (this.isSubscriberCorrectToSubscriberOrganization(this.selectedLookupItem.organization)) {
                            this.isAddButtonDisabled = false;
                            this.showExistingSubscriberMessage = false;
                        } else {
                            this.isAddButtonDisabled = true;
                        }
                    }
                });
        },
        isSubscriberCorrectToSubscriberOrganization(subscriberOrganization) {
            if (!this.supplier.legalIdentity || !this.supplier.legalIdentity.type || !this.supplier.legalIdentity.value) {
                return false;
            }

            return undefined !== subscriberOrganization.legalIdentities.find(le => {
                return le.type.id === this.supplier.legalIdentity.type.id && le.value === this.supplier.legalIdentity.value;
            });
        },
        checkExistingSupplier() {
            this.showLoading = true;
            if (!this.supplier.title && !this.supplier.legalIdentity) {
                this.checkSuppliersDomain();
            } else {
                this
                    .searchExistingSupplier({
                        title: this.supplier.title,
                        legalIdentity: this.supplier.legalIdentity
                    })
                    .then(data => {
                        this.existingSupplier = null;
                        this.isAddButtonDisabled = false;
                        if (data.exists) {
                            this.showExistingSubscriberMessage = false;
                            this.showExistingSupplierMessage = true;
                            this.existingSupplierMessageVariant = 'danger';
                            this.isAddButtonDisabled = true;
                            this.existingSupplierMessage = this.$t('Component(Text):supplier-with-name-or-id-exists');
                            this.existingSupplier = data.supplier;
                        } else if (this.supplier.domain) {
                            return this.checkSuppliersDomain();
                        } else {
                            this.showExistingSupplierMessage = false;
                            return this.lookupSubscriber();
                        }
                    })
                    .finally(() => this.showLoading = false)
                ;
            }
        },
        useExistingSupplier() {
            const supplier = {...this.existingSupplier};
            this.reinit();
            this.$emit('saved', supplier);
            this.hideModal();
        },
        useExistingSubscriberOrganization() {
            this.supplier.title = this.selectedLookupItem.organization.title;
            this.supplier.legalIdentity = this.selectedLookupItem.organization.legalIdentities[0];

            if (!this.supplier.domain && this.selectedLookupItem.organization.domains.length > 0) {
                this.supplier.domain = this.selectedLookupItem.organization.domains[0];
            }
            this.isAddButtonDisabled = false;
            this.showExistingSubscriberMessage = false;
        },
        save(event) {
            event.preventDefault();
            //  todo i do not like this solution, would be good to refactor it
            this.contact.communicationChannels.push(this.communicationChannel);
            this.supplier.contacts = [this.contact];

            this.$refs.legalIdentity.validate();
            this.$refs.supplier.validate().then(isValid => {
                if (!this.supplier.state) {
                    this.stateErrorMessage = 'State field is required.'
                    return
                }
                this.stateErrorMessage = null;
                if (isValid) {
                    this.doSave();
                }
            });
        },
        searchExistingSupplier(supplier) {
            return SupplyChain.exists(supplier).then(r => r.data);
        },
        doSave() {
            this.showLoading = true;
            const supplier = {
                ...this.supplier
            };
            //  copy paste
            if (supplier.legalIdentity.type) {
                supplier.legalIdentity = {
                    type: supplier.legalIdentity.type.id,
                    value: supplier.legalIdentity.value
                };
            }
            this.domainError = '';
            SupplyChain
                .newSupplier(supplier)
                .then(r => {
                    // this.selectedSupplier = this.supplier;
                    this.reinit();
                    this.$emit('saved', r.data);
                    this.hideModal();
                })
                .catch(e => {
                    this.contact.communicationChannels = [];
                    this.supplier.contacts = [];
                    if (e.response && e.response.status === 422 && e.response.data) {
                        this.legalIdentityErrors = e.response.data
                            .filter(v => v.code === 'system:legal-identity:invalid')
                            .map(v => v.message);

                        const domainErrors = e.response.data
                            .filter(v => v.propertyPath === 'domain')
                            .map(v => v.message);

                        if (domainErrors.length > 0) {
                            this.domainError = domainErrors[0];
                        }

                        const restMessages = e.response.data
                            .filter(v => {
                                if (v.code === 'system:legal-identity:invalid') {
                                    return false;
                                }
                                return v.propertyPath !== 'domain';

                            });
                        if (restMessages.length > 0) {
                            this.showViolationErrors(restMessages);
                        }
                    } else {
                        this.handleBackendError(e);
                    }
                })
                .finally(() => this.showLoading = false);
        },
        showModal() {
            this.$refs['supplier-modal'].show();
        },
        hideModal() {
            this.$refs['supplier-modal'].hide();
        },

        reinit() {
            this.contact = this.emptyContact();
            this.supplier = this.emptySupplier();
            this.communicationChannel = this.emptyChannel();
            this.existingSupplier = null;
            this.existingSupplierMessage = null;
            this.showExistingSupplierMessage = false;
            this.showExistingSubscriberMessage = false;
            this.isSubscriberFullyMatched = false;
            this.selectedLookupItem = null;
            this.subscriberLookupResult = [];
            this.legalIdentityErrors = [];
            this.domainError = '';
        },

        onModalClose() {
            this.reinit();
        },

        emptyContact() {
            return {
                name: null,
                communicationChannels: []
            };
        },

        emptyChannel() {
            return {
                type: CommunicationType.EMAIL,
                channel: null
            };
        },

        emptySupplier() {
            return {
                title: null,
                state: null,
                domain: null,
                legalIdentity: null
            };
        },
        normalize(node) {
            return {id: node.id, label: node.title};
        },
        initStates(states) {
            states.forEach(this.initState);
            this.states = states;
        },
        initState(state) {
            if (state.children === undefined) {
                return;
            }
            if (null === state.children || state.children.length === 0) {
                delete state.children;
            } else {
                state.children.forEach(this.initState);
            }
        },
    },
    computed: {
        existingSubscriberOrganizationMessage() {
            if (this.isSubscriberFullyMatched) {
                return this.$t('Component(SupplierSelector):subscriber-exists', {
                    name: this.selectedLookupItem.organization.title
                });
            }

            return this.$t('Component(SupplierSelector):subscriber-partially-matched');
        }
    },
    created() {
        SupplyChain.getStates().then(r => {
            this.initStates(r.data);
        });
    },
}
</script>

<style lang="scss">
@import '../../../../node_modules/@riophae/vue-treeselect/dist/vue-treeselect.css';
</style>
